import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { BarChart3, Eye, PackagePlus, ShoppingBag, UserPlus, type LucideIcon } from 'lucide-react';
import { PERMISSIONS } from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Card, CardHeader } from '@/components/ui/Card';
import { PageHeader } from '@/components/ui/PageHeader';
import { usePermissions } from '@/hooks/usePermissions';
import { useOverviewQuery } from '@/modules/reports/api/reports.api';
import { OverviewContent, OverviewSkeleton } from '@/modules/reports/components/OverviewContent';
import { ReportMetaLine } from '@/modules/reports/components/ReportMetaLine';
import { apiErrorMessage } from '@/services/apiClient';
import { useAuthStore } from '@/store/authStore';

interface QuickAction {
  key: string;
  to: string;
  icon: LucideIcon;
  permission: string;
}

const QUICK_ACTIONS: QuickAction[] = [
  { key: 'newSale', to: '/sales/counter', icon: ShoppingBag, permission: PERMISSIONS.SALES_CREATE },
  {
    key: 'newCustomer',
    to: '/customers',
    icon: UserPlus,
    permission: PERMISSIONS.CUSTOMERS_CREATE,
  },
  {
    key: 'examination',
    to: '/customers/examinations',
    icon: Eye,
    permission: PERMISSIONS.MEDICAL_CREATE,
  },
  {
    key: 'receiveStock',
    to: '/inventory/stock',
    icon: PackagePlus,
    permission: PERMISSIONS.INVENTORY_ADJUST,
  },
];

/** The live Overview for this month, from the reporting API. Only mounted for `reporting.view`. */
function DashboardOverview() {
  const { t } = useTranslation();
  const overview = useOverviewQuery({ preset: 'THIS_MONTH', compare: 'previous' });

  return (
    <section aria-labelledby="dashboard-overview" className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 id="dashboard-overview" className="text-ink text-sm font-semibold">
          {t('dashboard.thisMonth')}
        </h2>
        <Link
          to="/reports"
          className="text-brand-700 inline-flex items-center gap-1 text-sm font-medium"
        >
          <BarChart3 className="size-4" aria-hidden />
          {t('dashboard.openReports')}
        </Link>
      </div>
      {overview.data ? <ReportMetaLine meta={overview.data.meta} /> : null}
      {overview.isError ? (
        <Alert tone="error">{apiErrorMessage(overview.error) ?? t('reports.loadError')}</Alert>
      ) : null}
      {overview.isPending ? <OverviewSkeleton /> : null}
      {overview.data ? <OverviewContent report={overview.data} /> : null}
    </section>
  );
}

/**
 * The home page. Figures come only from the reporting API and only for users holding
 * `reporting.view`; everyone else sees their quick actions and nothing pretending to be a number.
 */
export function DashboardPage() {
  const { t } = useTranslation();
  const { can } = usePermissions();
  const user = useAuthStore((state) => state.user);

  if (!user) return null;

  const quickActions = QUICK_ACTIONS.filter((action) => can(action.permission));
  const canReport = can(PERMISSIONS.REPORTING_VIEW);

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('dashboard.title')}
        subtitle={t('dashboard.welcome', { name: user.firstName })}
      />

      {canReport ? <DashboardOverview /> : null}

      <Card>
        <CardHeader
          title={t('dashboard.quickActions.title')}
          subtitle={t('dashboard.quickActions.subtitle')}
        />

        {quickActions.length === 0 ? (
          <p className="text-muted p-5 text-sm">{t('dashboard.quickActions.none')}</p>
        ) : (
          <ul className="grid gap-3 p-5 sm:grid-cols-2 xl:grid-cols-4">
            {quickActions.map((action) => (
              <li key={action.key}>
                <Link
                  to={action.to}
                  className="border-line hover:border-brand-300 hover:bg-brand-50/50 flex items-center gap-3 rounded-lg border px-4 py-3 transition-colors"
                >
                  <span className="bg-brand-50 text-brand-600 grid size-9 shrink-0 place-items-center rounded-lg">
                    <action.icon className="size-4.5" aria-hidden />
                  </span>
                  <span className="text-ink min-w-0 flex-1 truncate text-sm font-medium">
                    {t(`dashboard.quickActions.${action.key}`)}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
